import React, { useMemo } from "react";
import { AspectRatio, Stack, Text } from "@mantine/core";
import { EyeCoverAction, Position } from "types/testTypes";
import { useTranslation } from "react-i18next";
import { getTitleForCoverAction } from "@utils/UIUtils";
import { ScatterChart } from "@mantine/charts";

interface CoverTestEyePathChartAngleProps {
  points: Position[];
  eyeCoverAction: EyeCoverAction;
  style?: React.CSSProperties;
  isGazeAngle?: boolean;
}

const getColorForAction = (action: EyeCoverAction): string => {
  switch (action) {
    case EyeCoverAction.CoverLeftEyeRightKeptOpen:
    case EyeCoverAction.OpenRightEyeLeftKeptOpen:
      return "red";
    case EyeCoverAction.CoverRightEyeLeftKeptOpen:
    case EyeCoverAction.OpenLeftEyeRightKeptOpen:
      return "blue";
    default:
      return "gray";
  }
};

const CoverTestEyePathChartAngle: React.FC<CoverTestEyePathChartAngleProps> = ({
  points = [],
  eyeCoverAction,
  style,
  isGazeAngle = true,
}) => {
  const { t } = useTranslation();

  const MAX_GAZE_ANGLE = 20;
  const MAX_POSITION_VALUE = 0.1;
  const DEGREE_AXIS_MARGIN_FACTOR = 1.25;

  const titleKey = useMemo(() => getTitleForCoverAction(eyeCoverAction), [eyeCoverAction]);
  const lineColor = useMemo(() => getColorForAction(eyeCoverAction), [eyeCoverAction]);

  function mapPointsToScatterData(points: Position[]) {
    return points.map((point) => ({
      x: point.x,
      y: point.y,
    }));
  }

  const scatterData = useMemo(() => mapPointsToScatterData(points), [points]);

  const degreeAxisLimit = useMemo(() => {
    if (!isGazeAngle || points.length === 0) {
      return MAX_GAZE_ANGLE;
    }

    const pointMax = points.reduce(
      (max, point) => Math.max(max, Math.abs(point.x), Math.abs(point.y)),
      0,
    );

    const paddedMax = pointMax * DEGREE_AXIS_MARGIN_FACTOR;
    return Math.max(1, Math.min(MAX_GAZE_ANGLE, Math.ceil(paddedMax)));
  }, [isGazeAngle, points]);

  const XY_MIN_VALUE = isGazeAngle ? -degreeAxisLimit : -MAX_POSITION_VALUE;
  const XY_MAX_VALUE = isGazeAngle ? degreeAxisLimit : MAX_POSITION_VALUE;

  const createAxisProps = (minVal: number, maxVal: number) => {
    const domainMin = Math.min(minVal, 0);
    const domainMax = Math.max(maxVal, 0);

    const minTick = Math.ceil(domainMin);
    const maxTick = Math.floor(domainMax);

    const maxDesiredTicks = 9;
    const range = Math.max(0, maxTick - minTick);
    let step = 1;
    if (range > 0 && range + 1 > maxDesiredTicks) {
      step = Math.ceil((range + 1) / (maxDesiredTicks - 1));
    }

    const ticks: number[] = [];
    if (minTick <= maxTick) {
      for (let i = minTick; i <= maxTick; i += step) ticks.push(i);
      if (ticks[ticks.length - 1] !== maxTick) ticks.push(maxTick);
    }

    if (!ticks.includes(0)) ticks.push(0);

    return {
      domain: [domainMin, domainMax],
      padding: { left: 0, right: 0, top: 0, bottom: 0 },
      ticks: Array.from(new Set(ticks)).sort((a, b) => a - b),
      tickFormatter: (v: number) => `${v}`,
    };
  };

  const axisProps = createAxisProps(XY_MIN_VALUE, XY_MAX_VALUE);

  return (
    <Stack>
      <Text ta="start" size="sm">
        {t(titleKey)}
      </Text>
      <AspectRatio ratio={1} style={{ width: "100%", ...style }}>
        <ScatterChart
          h={200}
          w={200}
          data={scatterData ? [{ name: "Eye Path", color: lineColor, data: scatterData }] : []}
          gridAxis={"xy"}
          xAxisProps={axisProps}
          yAxisProps={axisProps}
          dataKey={{ x: "x", y: "y" }}
          yAxisLabel={isGazeAngle ? `${t("elevation")}°` : ""}
          unit={{ x: isGazeAngle ? "°" : "", y: isGazeAngle ? "°" : "" }}
          xAxisLabel={isGazeAngle ? `${t("azimuth")}°` : ""}
          styles={{
            axisLabel: { gap: 0 },
          }}
          scatterProps={{ shape: <circle r={2} /> }}
        />
      </AspectRatio>
    </Stack>
  );
};

export default React.memo(CoverTestEyePathChartAngle);
